import React, { useState, useMemo, useEffect, useRef } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import {
  Button,
  Stack,
  Box,
  Tabs,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { getUser } from '@/lib/common';
import { Loader } from '@/components/loader/loader';
import { useGlobalBudget } from '@/screens/budgets/components/hooks/useGlobalBudget';
import { AppCard } from '@/components/app-card/app-card';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import TabPanel, { a11yProps } from '@/components/tab-panel/tab-panel';
import AnnualBudgetConsumptionChart from '../charts/annual-budget-consumption-chart';
import MonthlyBudgetConsumptionChart from '../charts/monthly-budget-consumption-chart';

import { useDepartments, Department } from './hooks/useDepartments';
import {
  getSavedBudgetValue,
  getSavedBudgetCurrency,
  SystemData,
} from '../calculations';
import { BudgetFormData } from '../types';
import { BudgetChangeModal, BudgetChangeData } from './budget-change-modal';
import { PermissionArea } from '@cloudcost/shared-types/users';
import { hasUserAccessTo } from '@/lib/permissions';
import { useSystems } from './hooks/useSystems';
import { OwnedSystemsTabs } from './systems/owned-systems-tabs';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { DisputeDialog } from '@/components/dispute-dialog/dispute-dialog';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { UserDto } from '@cloudcost/shared-types/users';
import DisputesModal from '@/components/disputes-table/disputes-modal';
import { useActionCenterViewContext } from '@/contexts/action-center-view-context';

interface DepartmentsBudgetProps {
  initialDepartmentId?: number;
  initialSystemId?: number;
}

export default function DepartmentsBudget({ initialDepartmentId, initialSystemId }: DepartmentsBudgetProps = {}) {
  const [budgetValue, setBudgetValue] = useState<BudgetFormData>({
    id: 0,
    name: '',
    original_currency_value: '',
    original_currency: null,
  });
  const [budgetRequestModalOpen, setBudgetRequestModalOpen] = useState(false);
  const [selectedDepartmentForRequest, setSelectedDepartmentForRequest] =
    useState<Department | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [sponsorName, setSponsorName] = useState<string>('');
  const [sponsorId, setSponsorId] = useState<number>();

  // Systems state
  const [systemBudgetRequestModalOpen, setSystemBudgetRequestModalOpen] =
    useState(false);
  const [selectedSystemForRequest, setSelectedSystemForRequest] =
    useState<SystemData | null>(null);
  const [systemsTabValue, setSystemsTabValue] = useState(0);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [systemDisputeDialogOpen, setSystemDisputeDialogOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<UserDto[]>([]);
  const [selectedDepartmentForDispute, setSelectedDepartmentForDispute] =
    useState<Department | null>(null);
  const [selectedSystemForDispute, setSelectedSystemForDispute] =
    useState<SystemData | null>(null);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [dispute, setDispute] = useState<any[]>([]);

  const user = getUser();
  const { clearSelection } = useActionCenterViewContext();
  
  // Track if we've already processed initialDepartmentId to prevent re-setting on manual tab changes
  const hasProcessedInitialDepartmentId = useRef(false);
  const lastProcessedDepartmentId = useRef<number | undefined>(undefined);

  const { globalBudget } = useGlobalBudget();
  const { departmentsList, users, loading, refreshData } = useDepartments();
  const {
    items: systemsList,
    loading: systemsLoading,
    refreshData: refreshSystemsData,
  } = useSystems();

  useEffect(() => {
    if (users.length > 0) {
      const sponsor = users.find((user) => user.is_sponsor);
      if (sponsor) {
        setSponsorName(sponsor.firstName + ' ' + sponsor.lastName);
        setSponsorId(sponsor.id);
      }
    }
  }, [users]);

  // Fetch users for dispute dialog (light list — full list is admin-only)
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const usersData = await fetchWrapper.get(urls.DevOps.getUsersFilter);
        setAllUsers(usersData);
      } catch (error) {
        console.error('Error fetching all users:', error);
      }
    };
    fetchAllUsers();
  }, []);

  // Update budgetValue currency when globalBudget becomes available
  useEffect(() => {
    if (
      globalBudget?.original_currency &&
      budgetValue.original_currency === null
    ) {
      setBudgetValue((prev) => ({
        ...prev,
        original_currency: globalBudget.original_currency,
      }));
    }
  }, [globalBudget, budgetValue.original_currency]);

  // Determine which departments the user can see
  const canViewAllDepartments = hasUserAccessTo(
    PermissionArea.DEPARTMENT_BUDGET_VIEW_ALL,
  );

  const ownedDepartmentsList = useMemo(() => {
    if (canViewAllDepartments) {
      return departmentsList;
    }

    return departmentsList.filter((department) => {
      return department.id_owner === user?.id;
    });
  }, [departmentsList, user, canViewAllDepartments]);

  const [tabValue, setTabValue] = React.useState(0);

  // Get systems for the current selected department (defined early so it can be used in useEffect)
  const currentDepartmentSystems = useMemo(() => {
    const currentDepartment = ownedDepartmentsList[tabValue];
    if (!currentDepartment || !systemsList) {
      return [];
    }
    return systemsList.filter(
      (system) => system.department_id === currentDepartment.id,
    );
  }, [systemsList, tabValue, ownedDepartmentsList]);

  const getCurrentTabDepartment = (): Department | undefined => {
    return ownedDepartmentsList[tabValue];
  };

  // Deep link: Select department sub-tab when initialDepartmentId is provided
  useEffect(() => {
    // Only process if:
    // 1. We have an initialDepartmentId
    // 2. Departments are loaded
    // 3. We haven't processed this specific initialDepartmentId yet (or it's a new one)
    if (initialDepartmentId && ownedDepartmentsList.length > 0) {
      // If this is a new initialDepartmentId (different from last processed), reset the flag
      if (lastProcessedDepartmentId.current !== initialDepartmentId) {
        hasProcessedInitialDepartmentId.current = false;
        lastProcessedDepartmentId.current = initialDepartmentId;
      }
      
      // Only set if we haven't processed this initialDepartmentId yet
      if (!hasProcessedInitialDepartmentId.current) {
        const idx = ownedDepartmentsList.findIndex(dept => dept.id === initialDepartmentId);
        if (idx !== -1) {
          setTabValue(idx);
          hasProcessedInitialDepartmentId.current = true;
        } else {
          console.warn('[DepartmentsBudget] Department not found in ownedDepartmentsList:', initialDepartmentId);
          hasProcessedInitialDepartmentId.current = true; // Mark as processed even if not found to prevent retries
        }
      }
    } else if (!initialDepartmentId) {
      // Reset flags when initialDepartmentId is cleared
      hasProcessedInitialDepartmentId.current = false;
      lastProcessedDepartmentId.current = undefined;
    }
  }, [initialDepartmentId, ownedDepartmentsList]);

  // Deep link: Select system sub-tab when initialSystemId is provided (after department is selected)
  useEffect(() => {
    // Ensure we have a department selected and systems are loaded
    if (!initialSystemId) return;
    if (loading || systemsLoading) {
      return;
    }
    if (currentDepartmentSystems.length === 0) {
      return;
    }
    
    const idx = currentDepartmentSystems.findIndex(sys => sys.id === initialSystemId);
    if (idx !== -1) {
      setSystemsTabValue(idx);
      
      // Wait for department charts to render before scrolling to systems section
      // Annual forecast items may take longer to render charts
      const scrollToSystems = () => {
        const systemsElement = document.querySelector('[data-systems-section]');
        if (!systemsElement) {
          // If element not found yet, retry after a short delay
          setTimeout(scrollToSystems, 200);
          return;
        }
        
        // Check if charts are rendered by looking for chart containers
        const chartContainers = document.querySelectorAll('.card100');
        const hasCharts = chartContainers.length >= 2; // At least department card + 2 chart cards
        
        if (!hasCharts) {
          // Charts not rendered yet, wait a bit more
          setTimeout(scrollToSystems, 300);
          return;
        }
        
        // Recalculate position after charts have rendered (they might push content down)
        const elementPosition = systemsElement.getBoundingClientRect().top;
        const offsetPosition = window.pageYOffset + elementPosition - 24;
        
        window.scrollTo({
          top: Math.max(offsetPosition, 0),
          behavior: 'smooth'
        });
      };
      
      // Start scrolling after initial delay, with retry logic if charts aren't ready
      requestAnimationFrame(() => {
        setTimeout(scrollToSystems, 1000);
      });
    } else {
      console.warn('[DepartmentsBudget] System not found in currentDepartmentSystems:', {
        initialSystemId,
        currentDepartmentSystems: currentDepartmentSystems.map(s => ({ id: s.id, name: s.name })),
        currentDepartment: ownedDepartmentsList[tabValue]?.id,
      });
    }
  }, [initialSystemId, currentDepartmentSystems, loading, systemsLoading, tabValue, ownedDepartmentsList]);

  const handleChange = (_event: any, newValue: any) => {
    setTabValue(newValue);
    // Clear selection when user manually changes department tab
    clearSelection();
    // Reset the flag so initialDepartmentId won't override manual changes
    hasProcessedInitialDepartmentId.current = false;
    lastProcessedDepartmentId.current = undefined;
  };

  const handleBudgetRequestSubmit = async (data: BudgetChangeData) => {
    if (!selectedDepartmentForRequest) return;

    try {
      const budgetRequestId = await fetchWrapper.post(
        urls.DevOps.createDepartmentBudgetRequest,
        {
          departmentId: selectedDepartmentForRequest.id,
          currency: selectedDepartmentForRequest.original_currency || 'USD',
          newBudgetValue: data.newAnnualBudget,
          reason: data.reason,
          requesterId: user?.id || 1,
          approverId: sponsorId || 1,
        },
      );

      console.log('Department Budget Request Created!', budgetRequestId);
      refreshData();
      setRefreshTrigger((prev) => prev + 1);
      setBudgetRequestModalOpen(false);
      setSelectedDepartmentForRequest(null);
    } catch (error) {
      console.error('Error creating department budget request:', error);
    }
  };

  const handleSystemsTabChange = (_event: any, newValue: any) => {
    setSystemsTabValue(newValue);
    // Clear selection when user manually changes system tab
    clearSelection();
  };

  const getCurrentTabSystem = (): SystemData | undefined => {
    return currentDepartmentSystems[systemsTabValue];
  };

  const handleSystemBudgetRequestSubmit = async (data: BudgetChangeData) => {
    if (!selectedSystemForRequest) return;

    try {
      await fetchWrapper.post(urls.DevOps.createSystemBudgetRequest, {
        systemId: selectedSystemForRequest.id,
        currency: selectedSystemForRequest.original_currency || 'USD',
        newBudgetValue: data.newAnnualBudget,
        reason: data.reason,
        requesterId: user?.id,
        approverId: user?.id, // Department owner approves system budget changes
      });

      refreshSystemsData();
      setRefreshTrigger((prev) => prev + 1);
      setSystemBudgetRequestModalOpen(false);
      setSelectedSystemForRequest(null);
    } catch (error) {
      console.error('Error creating system budget request:', error);
    }
  };

  if (loading || systemsLoading) {
    return <Loader />;
  }

  return (
    <Stack spacing={2}>
      {/* DEPARTMENT OWNER VIEW */}
      {ownedDepartmentsList.length > 0 && (
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile>
              {ownedDepartmentsList.map((tab, index) => (
                <Tab key={tab.id} label={tab.name} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>

          {ownedDepartmentsList.map((tab, index) => (
            <TabPanel
              key={tab.id}
              value={tabValue}
              index={index}
              padding={0}
              paddingTop={3}>
              <Stack spacing={2}>
                <div data-department-section>
                  <AppCard
                    className="card100"
                    title="Department Budget"
                    icon={<BarChartOutlinedIcon color="primary" />}>
                    <Stack spacing={2}>
                    <Stack spacing={2} direction="row">
                      <TextField
                        label="Department Name"
                        variant="outlined"
                        disabled
                        size="small"
                        value={
                          getCurrentTabDepartment()
                            ? getCurrentTabDepartment()!.name
                            : 'No department selected'
                        }
                      />
                      <TextField
                        label="Department Owner"
                        variant="outlined"
                        disabled
                        size="small"
                        value={
                          (() => {
                            const department = getCurrentTabDepartment();
                            if (!department || !department.id_owner) {
                              return 'N/A';
                            }
                            const owner = users.find(
                              (u) => u.id === department.id_owner,
                            );
                            if (!owner) {
                              return 'N/A';
                            }
                            return `${owner.firstName} ${owner.lastName} (${owner.username})`;
                          })()
                        }
                      />
                    </Stack>
                    <Stack spacing={2} direction="row">
                      <TextField
                        label="Annual Department Budget"
                        variant="outlined"
                        disabled
                        size="small"
                        value={
                          getCurrentTabDepartment()
                            ? formatCurrencyNoUnit(parseFloat(getSavedBudgetValue(getCurrentTabDepartment()!)))
                            : 'No department selected'
                        }
                      />
                      <TextField
                        label="Budget Currency"
                        variant="outlined"
                        disabled
                        size="small"
                        value={
                          getCurrentTabDepartment()
                            ? getSavedBudgetCurrency(getCurrentTabDepartment()!)
                            : 'N/A'
                        }
                      />
                      {getCurrentTabDepartment() &&
                        getSavedBudgetValue(getCurrentTabDepartment()!) !==
                          'No budget set' && (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedDepartmentForRequest(
                                  getCurrentTabDepartment() as Department,
                                );
                                setBudgetRequestModalOpen(true);
                              }}
                              data-testid="request-budget-change">
                              Request Budget Change
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedDepartmentForDispute(
                                  getCurrentTabDepartment() as Department,
                                );
                                setDisputeDialogOpen(true);
                              }}>
                              Dispute department cost
                            </Button>
                          </>
                        )}
                    </Stack>
                  </Stack>
                </AppCard>
                </div>

                {getSavedBudgetValue(getCurrentTabDepartment()!) !==
                  'No budget set' && (
                  <>
                    <AppCard
                      className="card100"
                      title="Annual Budget Consumption"
                      icon={<BarChartOutlinedIcon color="primary" />}
                      headerRight={<CurrencyToggle budgetCurrency={getCurrentTabDepartment()?.original_currency || undefined} />}>
                      <AnnualBudgetConsumptionChart
                        budgetType="department"
                        entityId={getCurrentTabDepartment()?.id}
                        refreshTrigger={refreshTrigger}
                        budgetCurrency={getCurrentTabDepartment()?.original_currency || undefined}
                      />
                    </AppCard>

                    <AppCard
                      className="card100"
                      title="Monthly Budget Consumption"
                      icon={<BarChartOutlinedIcon color="primary" />}
                      headerRight={<CurrencyToggle budgetCurrency={getCurrentTabDepartment()?.original_currency || undefined} />}>
                      <MonthlyBudgetConsumptionChart
                        budgetType="department"
                        entityId={getCurrentTabDepartment()?.id}
                        refreshTrigger={refreshTrigger}
                        budgetCurrency={getCurrentTabDepartment()?.original_currency || undefined}
                      />
                    </AppCard>

                    <Typography
                      variant="h6"
                      data-systems-section
                      sx={{ mt: 12, mb: 0, textAlign: 'left' }}>
                      Systems in selected Department
                    </Typography>

                    {/* SYSTEMS */}
                    {currentDepartmentSystems.length > 0 && (
                      <OwnedSystemsTabs
                        ownedSystemList={currentDepartmentSystems}
                        tabValue={systemsTabValue}
                        onTabChange={handleSystemsTabChange}
                        getCurrentTabSystem={getCurrentTabSystem}
                        onRequestBudgetChange={(system) => {
                          setSelectedSystemForRequest(system);
                          setSystemBudgetRequestModalOpen(true);
                        }}
                        onDisputeSystemCost={(system) => {
                          setSelectedSystemForDispute(system);
                          setSystemDisputeDialogOpen(true);
                        }}
                        users={allUsers}
                      />
                    )}

                    {currentDepartmentSystems.length > 0 &&
                      getCurrentTabSystem() && (
                        <>
                          <AppCard
                            className="card100"
                            title="System Annual Budget Consumption"
                            icon={<BarChartOutlinedIcon color="primary" />}
                            headerRight={<CurrencyToggle budgetCurrency={getCurrentTabSystem()?.original_currency || undefined} />}>
                            <AnnualBudgetConsumptionChart
                              budgetType="system"
                              entityId={getCurrentTabSystem()?.id}
                              refreshTrigger={refreshTrigger}
                              budgetCurrency={getCurrentTabSystem()?.original_currency || undefined}
                            />
                          </AppCard>

                          <AppCard
                            className="card100"
                            title="System Monthly Budget Consumption"
                            icon={<BarChartOutlinedIcon color="primary" />}
                            headerRight={<CurrencyToggle budgetCurrency={getCurrentTabSystem()?.original_currency || undefined} />}>
                            <MonthlyBudgetConsumptionChart
                              budgetType="system"
                              entityId={getCurrentTabSystem()?.id}
                              refreshTrigger={refreshTrigger}
                              budgetCurrency={getCurrentTabSystem()?.original_currency || undefined}
                            />
                          </AppCard>
                        </>
                      )}
                  </>
                )}
              </Stack>
            </TabPanel>
          ))}
        </Box>
      )}

      <BudgetChangeModal
        open={budgetRequestModalOpen}
        onClose={() => {
          setBudgetRequestModalOpen(false);
          setSelectedDepartmentForRequest(null);
        }}
        onSubmit={handleBudgetRequestSubmit}
        budgetType="department"
        currentBudget={selectedDepartmentForRequest?.value || 0}
        budgetCurrency={selectedDepartmentForRequest?.original_currency || ''}
        entityName={selectedDepartmentForRequest?.name}
        departmentOwner={selectedDepartmentForRequest?.username}
        globalBudget={globalBudget?.original_currency_value}
        departmentsBudgetsSum={
          ownedDepartmentsList.reduce(
            (sum, dept) => sum + (dept.value || 0),
            0,
          ) - (selectedDepartmentForRequest?.value || 0)
        }
        isRequest={true}
        approverName={sponsorName}
      />

      <BudgetChangeModal
        open={systemBudgetRequestModalOpen}
        onClose={() => {
          setSystemBudgetRequestModalOpen(false);
          setSelectedSystemForRequest(null);
        }}
        onSubmit={handleSystemBudgetRequestSubmit}
        budgetType="system"
        currentBudget={selectedSystemForRequest?.value || 0}
        budgetCurrency={selectedSystemForRequest?.original_currency || ''}
        entityName={selectedSystemForRequest?.name}
        systemOwner={selectedSystemForRequest?.owner}
        departmentBudget={getCurrentTabDepartment()?.value}
        departmentsBudgetsSum={
          currentDepartmentSystems.reduce(
            (sum, sys) => sum + (sys.value || 0),
            0,
          ) - (selectedSystemForRequest?.value || 0)
        }
        isRequest={true}
        approverName={user?.firstName || 'Department Owner'}
      />

      <DisputeDialog
        open={disputeDialogOpen}
        onClose={() => {
          setDisputeDialogOpen(false);
          setSelectedDepartmentForDispute(null);
        }}
        onSubmit={async (title: string, receiverId: number) => {
          if (!selectedDepartmentForDispute) return;

          try {
            // Try to find the action center item for this budget
            let actionCenterId = null;
            try {
              const actionCenterItems = await fetchWrapper.get(urls.DevOps.getBudgetActionCenterV2);
              const matchingItem = actionCenterItems.find(
                (item: any) =>
                  item.type === 'Budget' &&
                  item.scope === 'Department' &&
                  item.id_budget === selectedDepartmentForDispute.budget_id &&
                  item.responsible_id === selectedDepartmentForDispute.id_owner
              );
              if (matchingItem) {
                actionCenterId = matchingItem.id;
              }
            } catch (error) {
              console.warn('Could not fetch action center items for dispute:', error);
            }

            await fetchWrapper.post(urls.DevOps.createDisputeV2, {
              id_receiver: receiverId,
              sender: user?.username,
              title: title,
              type: 'Budget',
              action_center_id: actionCenterId,
              id_budget: selectedDepartmentForDispute.budget_id || null,
            });

            // Get the created dispute and open it
            const maxDisputeData = await fetchWrapper.get(
              urls.DevOps.getMaxDispute,
            );
            const disputeId = maxDisputeData[0]?.disputeid;

            if (disputeId) {
              const response = await fetchWrapper.get(
                urls.DevOps.getDispute.replace(':id', String(disputeId)),
              );
              setDispute([response.dispute]);
              setDisputeModalOpen(true);
            }
          } catch (error) {
            console.error('Error creating dispute:', error);
          }
        }}
        defaultReceiverId={
          selectedDepartmentForDispute?.id_owner || null
        }
        currentUserId={user?.id || 0}
        showUserSelector={
          selectedDepartmentForDispute?.id_owner === user?.id
        }
        availableUsers={allUsers}
      />

      <DisputeDialog
        open={systemDisputeDialogOpen}
        onClose={() => {
          setSystemDisputeDialogOpen(false);
          setSelectedSystemForDispute(null);
        }}
        onSubmit={async (title: string, receiverId: number) => {
          if (!selectedSystemForDispute) return;

          try {
            // Try to find the action center item for this budget
            let actionCenterId = null;
            try {
              const actionCenterItems = await fetchWrapper.get(urls.DevOps.getBudgetActionCenterV2);
              const matchingItem = actionCenterItems.find(
                (item: any) =>
                  item.type === 'Budget' &&
                  item.scope === 'System' &&
                  item.id_budget === selectedSystemForDispute.budget_id &&
                  item.responsible_id === selectedSystemForDispute.owner_id
              );
              if (matchingItem) {
                actionCenterId = matchingItem.id;
              }
            } catch (error) {
              console.warn('Could not fetch action center items for dispute:', error);
            }

            await fetchWrapper.post(urls.DevOps.createDisputeV2, {
              id_receiver: receiverId,
              sender: user?.username,
              title: title,
              type: 'Budget',
              action_center_id: actionCenterId,
              id_budget: selectedSystemForDispute.budget_id || null,
            });

            // Get the created dispute and open it
            const maxDisputeData = await fetchWrapper.get(
              urls.DevOps.getMaxDispute,
            );
            const disputeId = maxDisputeData[0]?.disputeid;

            if (disputeId) {
              const response = await fetchWrapper.get(
                urls.DevOps.getDispute.replace(':id', String(disputeId)),
              );
              setDispute([response.dispute]);
              setDisputeModalOpen(true);
            }
          } catch (error) {
            console.error('Error creating dispute:', error);
          }
        }}
        defaultReceiverId={selectedSystemForDispute?.owner_id || null}
        currentUserId={user?.id || 0}
        showUserSelector={
          selectedSystemForDispute?.owner_id === user?.id
        }
        availableUsers={allUsers}
      />

      {disputeModalOpen && dispute[0] && (
        <DisputesModal
          Dispute={{
            ...(dispute[0] as any),
            id_dispute: (dispute[0] as any).id,
          }}
          IsModalOpened={disputeModalOpen}
          onCloseModal={() => {
            setDisputeModalOpen(false);
            setDispute([]);
          }}
        />
      )}
    </Stack>
  );
}
